import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { IconButton, TextField } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  ClipboardIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { useCopyToClipboard } from "components/common/useCopyToClipboard";
import { Inline } from "components/layout/reusableLayouts";

interface LinkModalProps {
  title: string;
  /** Already-translated copy, not a key: the caller composes it. */
  description: string;
  link: string;
  open: boolean;
  handleCloseModal: () => void;
}

const LinkModal: React.FC<LinkModalProps> = (props) => {
  const { title, description, link, open, handleCloseModal } = props;

  const { t } = useTranslation();
  const copy = useCopyToClipboard(t("Copied to clipboard"));

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Inline
          justify="between"
          align="start"
          gap="var(--space-5)"
          wrap={false}
        >
          {title}
          <IconButton
            size="small"
            variant="ghost"
            aria-label={t("Close")}
            onClick={handleCloseModal}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={20} />
          </IconButton>
        </Inline>
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ marginBottom: "var(--space-5)" }}>
          {description}
        </DialogContentText>
        <TextField
          // An explicit id keeps the DS out of React's useId, whose colons make
          // the generated rule an invalid selector once an sx nests one (R11).
          id="integration-link"
          aria-label={t("Integration link")}
          value={link}
          fullWidth
          // Read-only rather than disabled: a disabled input cannot be selected,
          // which left the copy button as the only route to the link.
          readOnly
          trailing={
            <IconButton
              size="small"
              variant="ghost"
              aria-label={t("Copy")}
              onClick={() => copy(link)}
            >
              <HugeiconsIcon icon={ClipboardIcon} size={16} />
            </IconButton>
          }
        />
      </DialogContent>

      <DialogActions>
        <Button variant="primary-outlined" onClick={() => copy(link)}>
          {t("Copy")}
        </Button>
        <Button
          variant="primary-filled"
          onClick={() => window.open(link, "_blank", "noopener,noreferrer")}
        >
          {t("Open")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkModal;
